import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, startWith, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';

import {
  loadEnvironmentalImpactDeliveriesCurrentYear,
  loadEnvironmentalImpactDeliveriesCurrentYearFailed,
  loadEnvironmentalImpactDeliveriesCurrentYearSuccess,
  loadEnvironmentalImpactDeliveriesLastYear,
  loadEnvironmentalImpactDeliveriesLastYearFailed,
  loadEnvironmentalImpactDeliveriesLastYearSuccess,
  loadEnvironmentalImpactWithBenchmark,
  loadEnvironmentalImpactWithBenchmarkFailed,
  loadEnvironmentalImpactWithBenchmarkNotFound,
  loadEnvironmentalImpactWithBenchmarkSuccess,
} from './environmental-impact.actions';
import { getEnvironmentalImpactsWithBenchmarks } from './environmental-impact.selectors';
import { cropYearChanged } from '../../../../store/actions';
import { selectCropYear } from '../../../../store/selectors';
import { EnvironmentalImpactDelivery, EnvironmentalImpactState } from '../environmental-impact.models';
import { EnvironmentalImpactService } from '../services/environmental-impact.service';

@Injectable()
export class EnvironmentalImpactEffects {
  private readonly actions$ = inject(Actions);
  private readonly environmentalImpactService = inject(EnvironmentalImpactService);
  private readonly store = inject<Store<EnvironmentalImpactState>>(Store);

  cropYearChanged$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(cropYearChanged),
      mergeMap(() => [loadEnvironmentalImpactDeliveriesCurrentYear(), loadEnvironmentalImpactDeliveriesLastYear()]),
    ),
  );

  loadEnvironmentalImpactWithBenchmark$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEnvironmentalImpactWithBenchmark),
      withLatestFrom(this.store.pipe(select(getEnvironmentalImpactsWithBenchmarks))),
      withLatestFrom(this.store.pipe(select(selectCropYear))),
      mergeMap(
        ([
          [
            {
              payload: { cropFieldId },
            },
            allReports,
          ],
          cropYear,
        ]) => {
          if (allReports.find((r) => r.environmentalImpactReport.cropFieldId === cropFieldId)) {
            return EMPTY;
          }
          return this.environmentalImpactService
            .fetchEnvironmentalImpactReportWithBenchmark(cropYear, cropFieldId)
            .pipe(
              mergeMap((report) =>
                report
                  ? [loadEnvironmentalImpactWithBenchmarkSuccess({ report })]
                  : [loadEnvironmentalImpactWithBenchmarkNotFound()],
              ),
              takeUntil(this.actions$.pipe(ofType(cropYearChanged))),
              catchError((error: HttpErrorResponse) => (error.status === 404 ? EMPTY : throwError(() => error))),
              startWith(CoreActions.setLoading({ loading: true })),
              finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
            );
        },
      ),
      catchError(() =>
        of(loadEnvironmentalImpactWithBenchmarkFailed({ message: 'errors.loadEnvironmentalImpactFailed' })),
      ),
    ),
  );

  loadEnvironmentalImpactDeliveriesCurrentYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEnvironmentalImpactDeliveriesCurrentYear),
      withLatestFrom(this.store.pipe(select(selectCropYear))),
      switchMap(([_, cropYear]) =>
        this.environmentalImpactService.fetchEnvironmentalImpactDeliveries(cropYear).pipe(
          map((deliveries: EnvironmentalImpactDelivery) =>
            loadEnvironmentalImpactDeliveriesCurrentYearSuccess({ deliveries }),
          ),
          catchError((error: HttpErrorResponse) => (error.status === 404 ? EMPTY : throwError(() => error))),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
      catchError(() =>
        of(loadEnvironmentalImpactDeliveriesCurrentYearFailed({ message: 'errors.loadEnvironmentalImpactFailed' })),
      ),
    ),
  );

  loadEnvironmentalImpactDeliveriesLastYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEnvironmentalImpactDeliveriesLastYear),
      withLatestFrom(this.store.pipe(select(selectCropYear))),
      switchMap(([_, cropYear]) =>
        this.environmentalImpactService.fetchEnvironmentalImpactDeliveries(cropYear - 1).pipe(
          map((deliveries: EnvironmentalImpactDelivery) =>
            loadEnvironmentalImpactDeliveriesLastYearSuccess({ deliveries }),
          ),
          catchError((error: HttpErrorResponse) => (error.status === 404 ? EMPTY : throwError(() => error))),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
      catchError(() =>
        of(loadEnvironmentalImpactDeliveriesLastYearFailed({ message: 'errors.loadEnvironmentalImpactFailed' })),
      ),
    ),
  );
}
